import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Card,
    CardContent,
    RadioGroup,
    FormControlLabel,
    Radio,
    Alert,
    IconButton,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
} from '@mui/material';
import {
    Close as CloseIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    Send as SendIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import TaxAuthorityService from '../../Services/taxAuthority.service';

/**
 * Tax Authority decision / pending-decision modal.
 *
 * Props:
 * - open, onClose, caseId, token
 * - onOptionSelect(decisionFlag, options) — used when resolvePending=false (legacy retry-create path)
 * - pendingStatus — optional status from getPendingInvoiceDecisionStatus
 * - resolvePending — when true, confirm calls resolvePending API using first locked payload
 * - onResolved({ success, action, decisionFlag, result }) — after successful resolvePending
 * - isInvoiceReceipt, invoiceLabel
 */
const TaxAuthorityErrorModal = ({
    open,
    onClose,
    onOptionSelect,
    invoiceData,
    caseId,
    token,
    isInvoiceReceipt = false,
    invoiceLabel = '',
    pendingStatus = null,
    resolvePending = false,
    onResolved = null,
}) => {
    const { t, i18n } = useTranslation();
    const [selectedOption, setSelectedOption] = React.useState('');
    const [isProcessing, setIsProcessing] = React.useState(false);

    const isRTL = i18n.language === 'he' || i18n.language === 'ar';
    const direction = isRTL ? 'rtl' : 'ltr';

    React.useEffect(() => {
        if (open) {
            setSelectedOption('');
            setIsProcessing(false);
        }
    }, [open, pendingStatus?.invoiceId]);

    const handleOptionSelect = (option) => {
        setSelectedOption(option);
        switch (option) {
            case 'not_making':
                toast.info(t('Option1Selected'));
                break;
            case 'continue_no_confirmation':
                toast.info(t('Option2Selected'));
                break;
            case 'mandatory_reversal':
                toast.info(t('Option3Selected'));
                break;
            case 'request_for_use':
                toast.info(t('Option4Selected'));
                break;
            default:
                break;
        }
    };

    const formatAmount = (v) => {
        // System.Text.Json used to corrupt Newtonsoft JObject numbers into [] → Number([]) === 0 in JS
        if (v === null || v === undefined || v === '' || Array.isArray(v)) return '—';
        if (typeof v === 'object') return '—';
        const n = Number(v);
        return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(v);
    };

    const formatDateTime = (value) => {
        if (!value) return '—';
        try {
            return new Date(value).toLocaleString();
        } catch {
            return String(value);
        }
    };

    const firstAttempt = pendingStatus?.firstAttempt || pendingStatus?.FirstAttempt;
    const requestPayload = firstAttempt?.requestPayload || firstAttempt?.RequestPayload || null;
    const invoicePayload = firstAttempt?.invoicePayload || firstAttempt?.InvoicePayload || null;
    const lockedInvoiceId = pendingStatus?.invoiceId || pendingStatus?.InvoiceId || '';
    const lockedErrorCode = firstAttempt?.errorCode ?? firstAttempt?.ErrorCode;
    const lockedErrorMessage = firstAttempt?.errorMessage || firstAttempt?.ErrorMessage || '';

    const confirmationHistory = React.useMemo(() => {
        const list = pendingStatus?.confirmationHistory || pendingStatus?.ConfirmationHistory || [];
        return Array.isArray(list) ? list : [];
    }, [pendingStatus]);

    const decisionHistory = React.useMemo(() => {
        const list = pendingStatus?.decisionHistory || pendingStatus?.DecisionHistory || [];
        return Array.isArray(list) ? list : [];
    }, [pendingStatus]);

    const pickAmount = (...candidates) => {
        for (const c of candidates) {
            if (c === null || c === undefined || c === '' || Array.isArray(c) || typeof c === 'object') continue;
            const n = Number(c);
            if (Number.isFinite(n)) return n;
        }
        return undefined;
    };

    const amountSummaryFromPayload = (payload) => {
        if (!payload || typeof payload !== 'object') return '—';
        const before = pickAmount(
            payload.amount_before_discount,
            payload.Amount_Before_Discount,
            payload.payment_amount,
            payload.Payment_Amount
        );
        const incl = pickAmount(
            payload.payment_amount_including_vat,
            payload.Payment_Amount_Including_VAT
        );
        const vat = pickAmount(payload.vat_amount, payload.VAT_Amount);
        if (before == null && incl == null && vat == null) return '—';
        return `${formatAmount(before)} / ${formatAmount(incl)} (${t('VAT') || 'VAT'} ${formatAmount(vat)})`;
    };

    const translateTaMessage = (msg) => {
        if (msg == null || msg === '') return '—';
        const raw = String(msg).trim();
        const lower = raw.toLowerCase();
        if (lower === 'invoice not approved') {
            return t('TaxInvoiceError460') || t('InvoiceNotApproved') || raw;
        }
        if (lower === 'customer vat number is incorrect') {
            return t('TaxInvoiceError432') || raw;
        }
        if (lower === 'decision accepted') {
            return t('DecisionAccepted') || raw;
        }
        if (lower === 'operation completed successfully') {
            return t('DecisionAccepted') || raw;
        }
        if (lower === 'decision failed') {
            return t('DecisionFailed') || raw;
        }
        if (lower === 'no matching unapproved invoice found') {
            return t('TaxInvoiceError463') || raw;
        }
        return raw;
    };

    const amountBeforeDiscount = pickAmount(
        requestPayload?.amount_before_discount,
        requestPayload?.Amount_Before_Discount,
        requestPayload?.payment_amount,
        requestPayload?.Payment_Amount,
        invoicePayload?.Items?.reduce?.((s, it) => s + Number(it.Price ?? it.price ?? 0) * Number(it.Quantity ?? it.quantity ?? 1), 0)
    );
    const paymentInclVat = pickAmount(
        requestPayload?.payment_amount_including_vat,
        requestPayload?.Payment_Amount_Including_VAT
    );
    const vatAmount = pickAmount(requestPayload?.vat_amount, requestPayload?.VAT_Amount);

    const rawItems = invoicePayload?.Items || invoicePayload?.items;
    const items = Array.isArray(rawItems)
        ? rawItems.filter((it) => it && typeof it === 'object' && !Array.isArray(it))
        : [];

    const requiresDecisionFlag = !!(pendingStatus?.requiresDecision || pendingStatus?.RequiresDecision);

    // A decision was accepted for this reference but its document was never issued. No new
    // decision can be sent (SHAAM answers 464), so the only way forward is to finish the document
    // on this same number — otherwise the reference stays blocked and no invoice can be issued.
    const statusValue = pendingStatus?.status || pendingStatus?.Status || '';
    const awaitingDocument = statusValue === 'AwaitingDocument';
    const acceptedDecisionAction =
        pendingStatus?.acceptedDecisionAction || pendingStatus?.AcceptedDecisionAction || '';
    const acceptedDecisionFlag = TaxAuthorityService.mapOptionToDecisionFlag(acceptedDecisionAction);
    const canFinishPendingDocument =
        awaitingDocument && !!lockedInvoiceId && acceptedDecisionFlag !== null;

    const handleFinishPendingDocument = async () => {
        setIsProcessing(true);
        try {
            if (onResolved) {
                // Replay the accepted action as the decision flag so the server short-circuits the
                // already-accepted decision and issues the document on this exact reference.
                await onResolved({
                    success: true,
                    action: acceptedDecisionAction,
                    decisionFlag: acceptedDecisionFlag,
                    invoiceId: lockedInvoiceId,
                    result: null,
                });
            }
            onClose();
        } catch (error) {
            console.error('Error finishing pending document:', error);
            toast.error(error?.message || t('AnErrorOccurred') || 'An error occurred');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleConfirm = async () => {
        if (!selectedOption) {
            toast.warning(t('PleaseSelectOptionFirst'));
            return;
        }

        setIsProcessing(true);

        try {
            // Send the decision only as part of document generation. A separate resolvePending
            // call used to accept the decision at SHAAM, then GenerateIncomePdf sent it again,
            // SHAAM answered 464, and the PDF never existed.
            if (resolvePending && requiresDecisionFlag && onResolved) {
                const action = TaxAuthorityService.mapOptionToResolveAction(selectedOption);
                const decisionFlag = TaxAuthorityService.mapOptionToDecisionFlag(selectedOption);
                await onResolved({
                    success: true,
                    action,
                    decisionFlag,
                    invoiceId: lockedInvoiceId,
                    result: null,
                });
                onClose();
                return;
            }

            if (resolvePending && requiresDecisionFlag) {
                const action = TaxAuthorityService.mapOptionToResolveAction(selectedOption);
                const decisionFlag = TaxAuthorityService.mapOptionToDecisionFlag(selectedOption);
                const result = await TaxAuthorityService.resolvePendingInvoiceDecision(
                    {
                        caseId,
                        invoiceId: lockedInvoiceId,
                        action,
                        documentType:
                            pendingStatus?.documentType ||
                            pendingStatus?.DocumentType ||
                            'TaxInvoice',
                    },
                    token
                );

                if (!result?.success && result?.Success === false) {
                    throw new Error(result.message || result.Message || t('DecisionFailed') || 'Decision failed');
                }

                toast.success(
                    (() => {
                        const msg = result?.message || result?.Message || '';
                        if (String(msg).toLowerCase() === 'decision accepted') {
                            return t('DecisionAccepted') || msg;
                        }
                        if (String(msg).toLowerCase().includes('decision')) {
                            return t('TaxAuthorityDecisionSent') || t('DecisionProcessedSuccessfully') || msg;
                        }
                        return msg || t('DecisionAccepted') || t('DecisionProcessedSuccessfully') || 'Decision processed';
                    })()
                );
                if (onResolved) {
                    // Wait for parent (e.g. generate+save document) so loading stays visible.
                    // invoiceId is the reference the decision was accepted for: the parent must
                    // issue the document on that exact number, otherwise the next-number lookup
                    // would register a second held invoice at the Tax Authority.
                    await onResolved({
                        success: true,
                        action,
                        decisionFlag,
                        invoiceId: lockedInvoiceId,
                        result,
                    });
                }
                onClose();
                return;
            }

            // Legacy path: parent retries create with Decision flag
            switch (selectedOption) {
                case 'not_making':
                    toast.success(t('InvoiceCancelledSuccessfully'));
                    if (onOptionSelect) onOptionSelect(0);
                    onClose();
                    break;
                case 'continue_no_confirmation':
                    if (onOptionSelect) onOptionSelect(1);
                    onClose();
                    break;
                case 'mandatory_reversal':
                    if (onOptionSelect) onOptionSelect(2);
                    onClose();
                    break;
                case 'request_for_use':
                    if (onOptionSelect) onOptionSelect(3, { forceNoConfirmationNumber: true });
                    onClose();
                    break;
                default:
                    onClose();
                    break;
            }
        } catch (error) {
            console.error('Error handling option selection:', error);
            toast.error(error?.message || t('AnErrorOccurred') || 'An error occurred while processing your selection');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCancel = () => {
        setSelectedOption('');
        onClose();
    };

    const baseOptions = [
        {
            value: 'not_making',
            title: t('FirstOptionNotMakingInvoice'),
            description: t('FirstOptionDescription'),
            icon: <CancelIcon color="error" />,
        },
        {
            value: 'continue_no_confirmation',
            title: t('SecondOptionContinueNoConfirmation'),
            description: t('SecondOptionDescription'),
            icon: <CheckCircleIcon color="warning" />,
        },
        {
            value: 'mandatory_reversal',
            title: t('ThirdOptionMandatoryReversal'),
            description: t('ThirdOptionDescription'),
            icon: <RefreshIcon color="info" />,
        },
        {
            value: 'request_for_use',
            title: t('FourthOptionRequestForUse'),
            description: t('FourthOptionDescription'),
            icon: <SendIcon color="primary" />,
        },
    ];

    const options = isInvoiceReceipt
        ? baseOptions.filter((opt) => opt.value !== 'mandatory_reversal')
        : baseOptions;

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    minHeight: '600px',
                    maxHeight: '80vh',
                    borderRadius: '12px',
                    direction: direction,
                },
            }}
        >
            <DialogTitle
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px 24px 16px 24px',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    borderBottom: '1px solid #e0e0e0',
                    direction: direction,
                }}
            >
                <Box display="flex" alignItems="center" gap={2} style={{ direction: direction }}>
                    <WarningIcon color="error" fontSize="large" />
                    <Typography variant="h6" component="div" style={{ direction: direction }}>
                        {resolvePending
                            ? t('PendingTaxAuthorityDecision') || t('TaxAuthorityErrorNoApprovedInvoice')
                            : t('TaxAuthorityErrorNoApprovedInvoice')}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleCancel}
                    size="small"
                    disabled={isProcessing}
                    style={{
                        color: '#666',
                        position: 'absolute',
                        left: isRTL ? '8px' : 'auto',
                        right: isRTL ? 'auto' : '8px',
                        top: '8px',
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                style={{
                    padding: '24px',
                    overflowY: 'auto',
                    maxHeight: '60vh',
                    direction: direction,
                }}
            >
                <Alert severity="error" style={{ marginBottom: '16px', borderRadius: '8px', direction: direction }}>
                    <Typography variant="body1" fontWeight="bold" style={{ direction: direction }}>
                        {lockedErrorCode
                            ? `${t('ErrorCode') || 'Error'} ${lockedErrorCode}`
                            : t('ErrorCode461')}
                    </Typography>
                    <Typography variant="body2" style={{ direction: direction }}>
                        {translateTaMessage(lockedErrorMessage) !== '—'
                            ? translateTaMessage(lockedErrorMessage)
                            : t('FailedToCreateConfirmationNumber')}
                    </Typography>
                    {lockedInvoiceId ? (
                        <Typography variant="body2" style={{ direction: direction, marginTop: 8 }}>
                            {t('InvoiceReference') || 'Reference'}: <strong>{lockedInvoiceId}</strong>
                        </Typography>
                    ) : null}
                </Alert>

                {invoiceLabel && (
                    <Alert severity="info" icon={false} style={{ marginBottom: '16px', borderRadius: '8px', direction: direction }}>
                        <Typography variant="body2" fontWeight="bold" style={{ direction: direction, textAlign: isRTL ? 'right' : 'left' }}>
                            {invoiceLabel}
                        </Typography>
                    </Alert>
                )}

                {awaitingDocument && (
                    <Alert severity="info" style={{ marginBottom: '16px', borderRadius: '8px', direction: direction }}>
                        <Typography variant="body1" fontWeight="bold" style={{ direction: direction }}>
                            {t('AwaitingDocument') ||
                                t('TaxAuthorityDecisionSent') ||
                                'Decision sent — document not issued'}
                        </Typography>
                        <Typography variant="body2" style={{ direction: direction, marginTop: 4 }}>
                            {canFinishPendingDocument
                                ? t('AwaitingDocumentFinishHint') ||
                                  'A decision was already sent to the Tax Authority for this invoice number, so no new decision can be sent. Finish issuing the document on this same number to release it.'
                                : t('AwaitingDocumentBlockedHint') ||
                                  'A decision was already sent to the Tax Authority for this invoice number, but which decision could not be determined. Contact support before issuing further invoices.'}
                        </Typography>
                        {acceptedDecisionAction ? (
                            <Typography variant="body2" style={{ direction: direction, marginTop: 8 }}>
                                {t('ApprovedDecision') ||
                                    t('TaxAuthorityDecisionSent') ||
                                    'Decision sent'}
                                :{' '}
                                <strong>
                                    {t(`DecisionAction_${acceptedDecisionAction}`) || acceptedDecisionAction}
                                </strong>
                            </Typography>
                        ) : null}
                    </Alert>
                )}

                {(requestPayload || items.length > 0) && (
                    <Alert severity="warning" icon={false} style={{ marginBottom: '16px', borderRadius: '8px', direction: direction }}>
                        <Typography variant="subtitle2" fontWeight="bold" style={{ marginBottom: 8 }}>
                            {t('OriginalLockedInvoiceDetails') || 'Original locked invoice (must be used for decision)'}
                        </Typography>
                        <Typography variant="body2">
                            {t('AmountBeforeDiscount') || 'Amount before discount'}:{' '}
                            <strong>{formatAmount(amountBeforeDiscount)}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {t('PaymentIncludingVAT') || 'Payment incl. VAT'}:{' '}
                            <strong>{formatAmount(paymentInclVat)}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {t('VATAmount') || 'VAT'}:{' '}
                            <strong>{formatAmount(vatAmount)}</strong>
                        </Typography>
                        {items.length > 0 && (
                            <Box mt={1}>
                                <Typography variant="body2" fontWeight="bold">
                                    {t('Items') || 'Items'}:
                                </Typography>
                                {items.slice(0, 8).map((it, idx) => (
                                    <Typography key={idx} variant="body2">
                                        • {it.Name || it.name || it.Makat || it.makat || `#${idx + 1}`} —{' '}
                                        {formatAmount(it.Price ?? it.price)} × {it.Quantity ?? it.quantity ?? 1}
                                    </Typography>
                                ))}
                                {items.length > 8 && (
                                    <Typography variant="caption">+{items.length - 8} more</Typography>
                                )}
                            </Box>
                        )}
                    </Alert>
                )}

                {!!pendingStatus && (
                    <Box mb={2}>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            {t('ConfirmationAttemptsHistory') || 'Confirmation attempts (rejected / success)'}
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 2 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('Date') || 'Date'}</TableCell>
                                        <TableCell>{t('Status') || 'Status'}</TableCell>
                                        <TableCell>{t('ErrorCode') || 'Error'}</TableCell>
                                        <TableCell>{t('Amounts') || t('AmountBeforeDiscount') || 'Amounts'}</TableCell>
                                        <TableCell>{t('Message') || 'Message'}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {confirmationHistory.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5}>{t('NoData') || '—'}</TableCell>
                                        </TableRow>
                                    ) : (
                                        confirmationHistory.map((row) => {
                                            const ok = !!(row.isSuccess ?? row.IsSuccess);
                                            const payload = row.requestPayload || row.RequestPayload;
                                            return (
                                                <TableRow key={row.id || row.Id}>
                                                    <TableCell>
                                                        {formatDateTime(row.creationDate || row.CreationDate)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            color={ok ? 'success' : 'error'}
                                                            label={
                                                                ok
                                                                    ? t('Success') || 'Success'
                                                                    : t('Rejected') || 'Rejected'
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>{row.errorCode ?? row.ErrorCode ?? '—'}</TableCell>
                                                    <TableCell>{amountSummaryFromPayload(payload)}</TableCell>
                                                    <TableCell>
                                                        {ok
                                                            ? row.confirmationNumber ||
                                                              row.ConfirmationNumber ||
                                                              t('Success')
                                                            : translateTaMessage(
                                                                  row.errorMessage || row.ErrorMessage
                                                              )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                {t('DecisionsSentHistory') || 'Decisions sent'}
                            </Typography>
                            <TableContainer component={Paper} sx={{ maxHeight: 200, mb: 2 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('Date') || 'Date'}</TableCell>
                                            <TableCell>{t('DecisionAction') || 'Action'}</TableCell>
                                            <TableCell>{t('Status') || 'Status'}</TableCell>
                                            <TableCell>{t('Message') || 'Message'}</TableCell>
                                            <TableCell>{t('DecisionPayload') || 'What we sent'}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {decisionHistory.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5}>
                                                    {t('NoDecisionsSentYet') || 'No decisions sent yet'}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            decisionHistory.map((row) => {
                                                const ok = !!(row.isSuccess ?? row.IsSuccess);
                                                const action = row.action || row.Action || '';
                                                const payload = row.requestPayload || row.RequestPayload;
                                                const payloadText = payload ? JSON.stringify(payload) : '—';
                                                const msgDisplay = translateTaMessage(
                                                    row.apiMessage || row.ApiMessage || row.errorDetails || row.ErrorDetails
                                                );
                                            return (
                                                <TableRow key={row.id || row.Id}>
                                                    <TableCell>
                                                        {formatDateTime(row.creationDate || row.CreationDate)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {t(`DecisionAction_${action}`) || action || '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            color={ok ? 'success' : 'error'}
                                                            label={
                                                                ok
                                                                    ? t('DecisionAccepted') || 'Accepted'
                                                                    : t('DecisionRejected') || 'Rejected'
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>{msgDisplay}</TableCell>
                                                    <TableCell
                                                        sx={{
                                                            maxWidth: 260,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            fontFamily: 'monospace',
                                                            fontSize: 12,
                                                        }}
                                                        title={payloadText}
                                                    >
                                                        {payloadText}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {!awaitingDocument && (!resolvePending || requiresDecisionFlag) && (
                <>
                <Typography variant="h6" style={{ marginBottom: '20px', color: '#333', fontWeight: 600, direction: direction }}>
                    {t('PleaseSelectOneOfFollowingOptions')}
                </Typography>

                {!selectedOption && (
                    <Alert severity="info" style={{ marginBottom: '16px', borderRadius: '8px', direction: direction }}>
                        <Typography variant="body2" style={{ direction: direction }}>
                            {t('PleaseSelectOptionFirst')}
                        </Typography>
                    </Alert>
                )}

                <RadioGroup
                    value={selectedOption}
                    onChange={(e) => handleOptionSelect(e.target.value)}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        direction: direction,
                    }}
                >
                    {options.map((option) => (
                        <Card
                            key={option.value}
                            style={{
                                border: selectedOption === option.value ? '2px solid #1976d2' : '2px solid #e0e0e0',
                                borderRadius: '8px',
                                transition: 'all 0.3s ease',
                                cursor: isProcessing ? 'not-allowed' : 'pointer',
                                backgroundColor: selectedOption === option.value ? 'rgba(25, 118, 210, 0.05)' : 'white',
                                direction: direction,
                                width: '100%',
                                transform: selectedOption === option.value ? 'scale(1.02)' : 'scale(1)',
                                boxShadow:
                                    selectedOption === option.value
                                        ? '0 4px 8px rgba(25, 118, 210, 0.2)'
                                        : '0 2px 4px rgba(0,0,0,0.1)',
                                opacity: isProcessing ? 0.6 : 1,
                            }}
                            onClick={() => !isProcessing && handleOptionSelect(option.value)}
                        >
                            <CardContent
                                style={{
                                    padding: '16px',
                                    direction: direction,
                                    width: '100%',
                                }}
                            >
                                <FormControlLabel
                                    value={option.value}
                                    control={<Radio color="primary" disabled={isProcessing} />}
                                    label={
                                        <Box
                                            style={{
                                                width: '100%',
                                                direction: direction,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: isRTL ? 'flex-end' : 'flex-start',
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                style={{
                                                    marginBottom: '8px',
                                                    flexDirection: isRTL ? 'row-reverse' : 'row',
                                                    direction: direction,
                                                    width: '100%',
                                                    justifyContent: isRTL ? 'flex-end' : 'flex-start',
                                                }}
                                            >
                                                {option.icon}
                                                <Typography
                                                    variant="h6"
                                                    style={{
                                                        fontSize: '1.1rem',
                                                        fontWeight: 600,
                                                        color: '#333',
                                                        direction: direction,
                                                        textAlign: isRTL ? 'right' : 'left',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {option.title}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    color: '#666',
                                                    lineHeight: 1.6,
                                                    fontSize: '0.9rem',
                                                    direction: direction,
                                                    textAlign: isRTL ? 'right' : 'left',
                                                    wordBreak: 'break-word',
                                                    whiteSpace: 'normal',
                                                    overflowWrap: 'break-word',
                                                    width: '100%',
                                                }}
                                            >
                                                {option.description}
                                            </Typography>
                                        </Box>
                                    }
                                    style={{
                                        width: '100%',
                                        margin: 0,
                                        padding: 0,
                                        direction: direction,
                                        alignItems: isRTL ? 'flex-end' : 'flex-start',
                                    }}
                                />
                            </CardContent>
                        </Card>
                    ))}
                </RadioGroup>
                </>
                )}
            </DialogContent>

            <DialogActions
                style={{
                    padding: '16px 24px',
                    borderTop: '1px solid #e0e0e0',
                    backgroundColor: '#fafafa',
                    gap: '12px',
                    direction: direction,
                    flexDirection: isRTL ? 'row-reverse' : 'row',
                }}
            >
                <Button
                    onClick={handleCancel}
                    variant="outlined"
                    color="secondary"
                    disabled={isProcessing}
                    style={{ direction: direction }}
                >
                    {t('Cancel')}
                </Button>
                {canFinishPendingDocument && (
                <Button
                    onClick={handleFinishPendingDocument}
                    variant="contained"
                    color="primary"
                    disabled={isProcessing}
                    style={{ direction: direction }}
                    startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isProcessing
                        ? 'Processing...'
                        : `${t('FinishPendingInvoice') || 'Finish invoice'} ${lockedInvoiceId}`}
                </Button>
                )}
                {!awaitingDocument && (!resolvePending || requiresDecisionFlag) && (
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    disabled={!selectedOption || isProcessing}
                    style={{
                        direction: direction,
                        opacity: selectedOption && !isProcessing ? 1 : 0.6,
                        cursor: selectedOption && !isProcessing ? 'pointer' : 'not-allowed',
                    }}
                    startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isProcessing
                        ? 'Processing...'
                        : selectedOption
                          ? t('ConfirmSelection')
                          : t('PleaseSelectOptionFirst')}
                </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default TaxAuthorityErrorModal;
